import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { Layout } from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Calendar } from "../components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  CreditCard,
  FileText,
  CalendarIcon,
  Save,
  User,
  Users,
  CheckCircle,
  XCircle,
  DollarSign,
  Mail,
  RefreshCw,
  Search,
  AlertCircle,
} from "lucide-react";
import { cn } from "../lib/utils";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ClientFilePage = ({ user, settings }) => {
  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState("");
  const [selectedClient, setSelectedClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  // Payment state
  const [paymentData, setPaymentData] = useState({
    date_paiement: new Date(),
    honoraires: "",
    montant_paye: "",
    mode_reglement: "",
    personne_payeur: "",
    autre_payeur: "",
  });
  const [existingPayment, setExistingPayment] = useState(null);

  // Form tracking state - Principal
  const [principalFormData, setPrincipalFormData] = useState({
    signature_presentielle: false,
    date_signature: null,
    date_envoi: null,
    date_retour: null,
    signature_valable: null,
    motif_refus: "",
  });
  const [existingPrincipalForm, setExistingPrincipalForm] = useState(null);

  // Form tracking state - Spouse
  const [spouseFormData, setSpouseFormData] = useState({
    signature_presentielle: false,
    date_signature: null,
    date_envoi: null,
    date_retour: null,
    signature_valable: null,
    motif_refus: "",
  });
  const [existingSpouseForm, setExistingSpouseForm] = useState(null);

  useEffect(() => {
    fetchClients();
  }, []);

  useEffect(() => {
    let result = [...clients];
    
    // Filter by search term
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      result = result.filter(
        (c) =>
          c.nom.toLowerCase().includes(search) ||
          c.prenom.toLowerCase().includes(search) ||
          c.code_client?.toLowerCase().includes(search) ||
          c.ville.toLowerCase().includes(search)
      );
    }
    
    // Sort by created_at descending (most recent first)
    result.sort((a, b) => {
      const dateA = new Date(a.created_at || 0);
      const dateB = new Date(b.created_at || 0);
      return dateB - dateA;
    });
    
    setFilteredClients(result);
  }, [clients, searchTerm]);

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API}/clients`, {
        withCredentials: true,
      });
      setClients(response.data);
    } catch (error) {
      console.error("Error fetching clients:", error);
      toast.error("Erreur lors du chargement des clients");
    } finally {
      setLoading(false);
    }
  };

  const handleClientSelect = async (client) => {
    setSelectedClientId(client.client_id);
    setSelectedClient(client);
    setSearchTerm("");

    // Fetch existing payment
    try {
      const paymentsRes = await axios.get(`${API}/payments?client_id=${client.client_id}`, {
        withCredentials: true,
      });
      if (paymentsRes.data.length > 0) {
        const payment = paymentsRes.data[0];
        setExistingPayment(payment);
        setPaymentData({
          date_paiement: payment.date_paiement ? new Date(payment.date_paiement) : new Date(),
          honoraires: payment.honoraires?.toString() || "",
          montant_paye: payment.montant_paye?.toString() || "",
          mode_reglement: payment.mode_reglement || "",
          personne_payeur: payment.personne_payeur || "",
          autre_payeur: payment.autre_payeur || "",
        });
      } else {
        // No existing payment - start with empty form
        setExistingPayment(null);
        setPaymentData({
          date_paiement: null,
          honoraires: "",
          montant_paye: "",
          mode_reglement: "",
          personne_payeur: "",
          autre_payeur: "",
        });
      }
    } catch (error) {
      console.error("Error fetching payment:", error);
    }

    // Fetch existing forms
    try {
      const formsRes = await axios.get(`${API}/forms?client_id=${client.client_id}`, {
        withCredentials: true,
      });
      
      const principalForm = formsRes.data.find((f) => f.is_principal);
      const spouseForm = formsRes.data.find((f) => !f.is_principal);

      if (principalForm) {
        setExistingPrincipalForm(principalForm);
        setPrincipalFormData({
          signature_presentielle: principalForm.signature_presentielle || false,
          date_signature: principalForm.date_signature ? new Date(principalForm.date_signature) : null,
          date_envoi: principalForm.date_envoi ? new Date(principalForm.date_envoi) : null,
          date_retour: principalForm.date_retour ? new Date(principalForm.date_retour) : null,
          signature_valable: principalForm.signature_valable,
          motif_refus: principalForm.motif_refus || "",
        });
      } else {
        setExistingPrincipalForm(null);
        setPrincipalFormData({
          signature_presentielle: false,
          date_signature: null,
          date_envoi: null,
          date_retour: null,
          signature_valable: null,
          motif_refus: "",
        });
      }

      if (spouseForm) {
        setExistingSpouseForm(spouseForm);
        setSpouseFormData({
          signature_presentielle: spouseForm.signature_presentielle || false,
          date_signature: spouseForm.date_signature ? new Date(spouseForm.date_signature) : null,
          date_envoi: spouseForm.date_envoi ? new Date(spouseForm.date_envoi) : null,
          date_retour: spouseForm.date_retour ? new Date(spouseForm.date_retour) : null,
          signature_valable: spouseForm.signature_valable,
          motif_refus: spouseForm.motif_refus || "",
        });
      } else {
        setExistingSpouseForm(null);
        setSpouseFormData({
          signature_presentielle: false,
          date_signature: null,
          date_envoi: null,
          date_retour: null,
          signature_valable: null,
          motif_refus: "",
        });
      }
    } catch (error) {
      console.error("Error fetching forms:", error);
    }
  };

  const hasSpouse = selectedClient?.situation_familiale === "Marié(e)" || 
                    selectedClient?.situation_familiale === "Conjoint(e) de fait";
  
  const isSpouseNonResident = selectedClient?.conjoint?.residence === "Non Résident";

  const savePayment = async () => {
    if (!selectedClientId || !paymentData.honoraires) return;

    setSaving(true);
    try {
      // honoraires is now "honoraires payés" - montant_paye equals honoraires
      const montantPaye = parseFloat(paymentData.honoraires);
      
      // Personne payeur: default to "Requérant principal" for Célibataire, Séparé(e), Divorcé(e)
      const needsPersonnePayeur = selectedClient?.situation_familiale === "Marié(e)" || 
                                   selectedClient?.situation_familiale === "Conjoint(e) de fait";
      const personnePayeur = needsPersonnePayeur ? paymentData.personne_payeur : "Requérent principal";

      const dataToSend = {
        client_id: selectedClientId,
        date_paiement: paymentData.date_paiement.toISOString(),
        honoraires: parseFloat(paymentData.honoraires),
        montant_paye: montantPaye,
        mode_reglement: paymentData.mode_reglement,
        personne_payeur: personnePayeur,
        autre_payeur: paymentData.autre_payeur || null,
      };

      if (existingPayment) {
        await axios.put(`${API}/payments/${existingPayment.payment_id}`, dataToSend, {
          withCredentials: true,
        });
        toast.success("Paiement mis à jour");
        // Refresh payment data to confirm update
        const paymentsRes = await axios.get(`${API}/payments?client_id=${selectedClientId}`, {
          withCredentials: true,
        });
        if (paymentsRes.data.length > 0) {
          setExistingPayment(paymentsRes.data[0]);
        }
      } else {
        const response = await axios.post(`${API}/payments`, dataToSend, {
          withCredentials: true,
        });
        setExistingPayment(response.data);
        toast.success("Paiement enregistré");
      }
    } catch (error) {
      console.error("Error saving payment:", error);
      toast.error("Erreur lors de l'enregistrement du paiement");
    } finally {
      setSaving(false);
    }
  };

  const saveForm = async (isPrincipal) => {
    if (!selectedClientId) return;

    const formData = isPrincipal ? principalFormData : spouseFormData;
    const existingForm = isPrincipal ? existingPrincipalForm : existingSpouseForm;

    setSaving(true);
    try {
      const dataToSend = {
        client_id: selectedClientId,
        is_principal: isPrincipal,
        signature_presentielle: formData.signature_presentielle,
        date_signature: formData.date_signature?.toISOString() || null,
        date_envoi: formData.date_envoi?.toISOString() || null,
        date_retour: formData.date_retour?.toISOString() || null,
        signature_valable: formData.signature_valable,
        motif_refus: formData.motif_refus || null,
      };

      if (existingForm) {
        await axios.put(`${API}/forms/${existingForm.form_id}`, dataToSend, {
          withCredentials: true,
        });
        toast.success(`Formulaire ${isPrincipal ? "principal" : "conjoint"} mis à jour`);
      } else {
        const response = await axios.post(`${API}/forms`, dataToSend, {
          withCredentials: true,
        });
        if (isPrincipal) {
          setExistingPrincipalForm(response.data);
        } else {
          setExistingSpouseForm(response.data);
        }
        toast.success(`Formulaire ${isPrincipal ? "principal" : "conjoint"} enregistré`);
      }
    } catch (error) {
      console.error("Error saving form:", error);
      toast.error("Erreur lors de l'enregistrement du formulaire");
    } finally {
      setSaving(false);
    }
  };

  const renderFormSection = (isPrincipal, formData, setFormData) => (
    <div className="space-y-4">
      {/* Signature Type */}
      <div className="space-y-2">
        <Label>Type de signature</Label>
        <RadioGroup
          value={formData.signature_presentielle ? "presentielle" : "courrier"}
          onValueChange={(value) =>
            setFormData({
              ...formData,
              signature_presentielle: value === "presentielle",
              date_envoi: null,
              date_retour: null,
              signature_valable: null,
              motif_refus: "",
              date_signature: null,
            })
          }
          className="flex gap-4"
        >
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="presentielle" id={`presentielle-${isPrincipal}`} />
            <Label htmlFor={`presentielle-${isPrincipal}`} className="cursor-pointer">
              Signature présentielle
            </Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="courrier" id={`courrier-${isPrincipal}`} />
            <Label htmlFor={`courrier-${isPrincipal}`} className="cursor-pointer flex items-center gap-1">
              <Mail className="h-4 w-4" />
              Par courrier
            </Label>
          </div>
        </RadioGroup>
      </div>

      {/* Presentielle Fields */}
      {formData.signature_presentielle && (
        <div className="p-4 bg-muted/50 rounded-lg space-y-2">
          <Label>Date de la signature</Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  "w-full justify-start text-left font-normal",
                  !formData.date_signature && "text-muted-foreground"
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {formData.date_signature
                  ? format(formData.date_signature, "PPP", { locale: fr })
                  : "Sélectionner la date"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0">
              <Calendar
                mode="single"
                selected={formData.date_signature}
                onSelect={(date) => setFormData({ ...formData, date_signature: date })}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>
      )}

      {/* Courrier Fields */}
      {!formData.signature_presentielle && (
        <div className="p-4 bg-muted/50 rounded-lg space-y-4">
          <div className="space-y-2">
            <Label>Date d'envoi au client</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full justify-start text-left font-normal",
                    !formData.date_envoi && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {formData.date_envoi
                    ? format(formData.date_envoi, "PPP", { locale: fr })
                    : "Sélectionner la date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar
                  mode="single"
                  selected={formData.date_envoi}
                  onSelect={(date) => setFormData({ ...formData, date_envoi: date })}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>

          <div className="space-y-2">
            <Label>Date de retour client</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full justify-start text-left font-normal",
                    !formData.date_retour && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {formData.date_retour
                    ? format(formData.date_retour, "PPP", { locale: fr })
                    : "Sélectionner la date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar
                  mode="single"
                  selected={formData.date_retour}
                  onSelect={(date) => setFormData({ ...formData, date_retour: date })}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>

          {formData.date_retour && (
            <>
              <div className="space-y-2">
                <Label>Signature valable ?</Label>
                <RadioGroup
                  value={
                    formData.signature_valable === null
                      ? ""
                      : formData.signature_valable
                      ? "oui"
                      : "non"
                  }
                  onValueChange={(value) =>
                    setFormData({
                      ...formData,
                      signature_valable: value === "oui",
                      motif_refus: value === "oui" ? "" : formData.motif_refus,
                    })
                  }
                  className="flex gap-4"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="oui" id={`valable-oui-${isPrincipal}`} />
                    <Label htmlFor={`valable-oui-${isPrincipal}`} className="cursor-pointer flex items-center gap-1">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      Oui
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="non" id={`valable-non-${isPrincipal}`} />
                    <Label htmlFor={`valable-non-${isPrincipal}`} className="cursor-pointer flex items-center gap-1">
                      <XCircle className="h-4 w-4 text-red-600" />
                      Non
                    </Label>
                  </div>
                </RadioGroup>
              </div>

              {formData.signature_valable === false && (
                <div className="space-y-2">
                  <Label>Motif de refus</Label>
                  <Textarea
                    value={formData.motif_refus}
                    onChange={(e) => setFormData({ ...formData, motif_refus: e.target.value })}
                    placeholder="Indiquer le motif du refus..."
                  />
                </div>
              )}
            </>
          )}
        </div>
      )}

      <Button
        onClick={() => saveForm(isPrincipal)}
        disabled={saving}
        className="w-full"
      >
        {saving ? (
          <>
            <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
            Enregistrement...
          </>
        ) : (
          <>
            <Save className="h-4 w-4 mr-2" />
            Enregistrer le formulaire
          </>
        )}
      </Button>
    </div>
  );

  if (loading) {
    return (
      <Layout user={user} settings={settings}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} settings={settings}>
      <div className="space-y-6" data-testid="client-file-page">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-foreground">
            Gestion du Dossier Client
          </h1>
          <p className="text-muted-foreground mt-1">
            Paiement et suivi des formulaires
          </p>
        </div>

        {/* Conditional Display: Client List OR Full Screen Client Form */}
        {!selectedClientId ? (
          /* Client Selection - Clickable List */
          <Card>
            <CardHeader className="pb-3">
              <CardTitle>Sélection du client</CardTitle>
              <CardDescription>Cliquez sur un client pour le sélectionner</CardDescription>
            </CardHeader>
            <CardContent>
              {/* Search */}
              <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Rechercher par nom, prénom, code client..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="search-client-input"
                />
              </div>

              {/* Client List - Full height */}
              <ScrollArea className="h-[calc(100vh-320px)] min-h-96 rounded-md border">
                <div className="p-2">
                  {filteredClients.length === 0 ? (
                    <p className="text-center text-muted-foreground py-4">Aucun client trouvé</p>
                  ) : (
                    filteredClients.map((client) => (
                      <div
                        key={client.client_id}
                        onClick={() => handleClientSelect(client)}
                        className="p-3 rounded-md cursor-pointer transition-colors mb-1 hover:bg-muted"
                        data-testid={`client-select-${client.client_id}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <p className="font-medium">
                                {client.prenom} {client.nom}
                              </p>
                              <span className="text-xs font-mono text-muted-foreground">
                                {client.code_client}
                              </span>
                              {client.preparation_gratuite && (
                                <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700">
                                  Gratuit
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground">
                              {client.ville} • {client.situation_familiale}
                            </p>
                            <p className="text-xs text-muted-foreground/70">
                              Modifié: {client.updated_at 
                                ? format(new Date(client.updated_at), "dd/MM/yyyy HH:mm", { locale: fr })
                                : client.created_at 
                                  ? format(new Date(client.created_at), "dd/MM/yyyy HH:mm", { locale: fr })
                                  : "N/A"
                              }
                            </p>
                          </div>
                          {client.conjoint && (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
                              + Conjoint(e)
                            </span>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        ) : (
          /* Full Screen Client Form */
          <div className="space-y-6">
            {/* Selected Client Header with Back Button */}
            <Card className="bg-primary/5 border-primary/20">
              <CardContent className="py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setSelectedClientId(null);
                        setSelectedClient(null);
                        setExistingPayment(null);
                      }}
                    >
                      ← Retour à la liste
                    </Button>
                    <div>
                      <p className="text-sm text-muted-foreground">Client sélectionné:</p>
                      <p className="font-semibold text-lg">
                        {selectedClient?.prenom} {selectedClient?.nom}
                        <span className="ml-2 text-sm font-mono text-muted-foreground">
                          ({selectedClient?.code_client})
                        </span>
                        {selectedClient?.preparation_gratuite && (
                          <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700">
                            Préparation gratuite
                          </span>
                        )}
                      </p>
                      {selectedClient?.conjoint && (
                        <p className="text-sm text-blue-600 mt-1">
                          Conjoint(e): {selectedClient.conjoint.prenom} {selectedClient.conjoint.nom}
                          {selectedClient.conjoint.residence === "Non Résident" && (
                            <span className="ml-2 text-orange-600">(Non Résident)</span>
                          )}
                        </p>
                      )}
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {selectedClient?.ville} • {selectedClient?.situation_familiale}
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Payment and Forms Grid - Full Width */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Payment Section */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="h-5 w-5 text-primary" />
                  Paiement des honoraires
                </CardTitle>
                <CardDescription>
                  {selectedClient?.preparation_gratuite 
                    ? "Préparation gratuite - aucun paiement requis"
                    : existingPayment 
                      ? "Modifier le paiement existant" 
                      : "Enregistrer un nouveau paiement"
                  }
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Display preparation gratuite badge */}
                {selectedClient?.preparation_gratuite ? (
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-center">
                    <p className="text-green-700 font-medium">
                      ✓ Préparation gratuite
                    </p>
                    <p className="text-sm text-green-600 mt-1">
                      Aucun paiement n'est requis pour ce client
                    </p>
                  </div>
                ) : (
                  <>
                    {/* Display client's agreed fees */}
                    {selectedClient?.honoraires_convenus > 0 && (
                      <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                        <p className="text-sm text-blue-700">
                          <span className="font-medium">Honoraires convenus (à payer):</span>{" "}
                          <span className="font-bold">{selectedClient.honoraires_convenus.toFixed(2)} $</span>
                        </p>
                      </div>
                    )}

                    {/* Date du paiement */}
                    <div className="space-y-2">
                      <Label>Date du paiement *</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className={cn(
                              "w-full justify-start text-left font-normal",
                              !paymentData.date_paiement && "text-muted-foreground"
                            )}
                          >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {paymentData.date_paiement
                              ? format(paymentData.date_paiement, "PPP", { locale: fr })
                              : "Sélectionner une date"}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0">
                          <Calendar
                            mode="single"
                            selected={paymentData.date_paiement}
                            onSelect={(date) =>
                              setPaymentData({ ...paymentData, date_paiement: date })
                            }
                            initialFocus
                          />
                        </PopoverContent>
                  </Popover>
                </div>

                {/* Montant payé */}
                <div className="space-y-2">
                  <Label>Montant payé *</Label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="0.00"
                      value={paymentData.honoraires}
                      onChange={(e) =>
                        setPaymentData({ ...paymentData, honoraires: e.target.value })
                      }
                      className="pl-10 font-mono"
                      data-testid="input-montant-paye"
                    />
                  </div>
                </div>
                
                {/* Balance calculation: Solde = Honoraires convenus - Montant payé */}
                {selectedClient?.honoraires_convenus > 0 && paymentData.honoraires && (
                  (() => {
                    const honorairesConvenus = selectedClient.honoraires_convenus;
                    const montantPaye = parseFloat(paymentData.honoraires) || 0;
                    const solde = honorairesConvenus - montantPaye;
                    
                    if (solde < 0) {
                      // Paid MORE than convenus (overpayment) - GREEN with warning
                      return (
                        <div className="p-3 bg-green-600 rounded-lg">
                          <p className="text-white font-medium text-sm">
                            Paiement en trop: {Math.abs(solde).toFixed(2)} $
                          </p>
                        </div>
                      );
                    } else if (solde > 0) {
                      // Paid LESS than convenus (remaining balance) - RED
                      return (
                        <div className="p-3 bg-red-600 rounded-lg">
                          <p className="text-white font-medium text-sm">
                            Reste à payer: {solde.toFixed(2)} $
                          </p>
                        </div>
                      );
                    } else {
                      // Exact amount paid - GREEN
                      return (
                        <div className="p-3 bg-green-600 rounded-lg">
                          <p className="text-white font-medium text-sm">
                            Paiement total - Solde: 0.00 $
                          </p>
                        </div>
                      );
                    }
                  })()
                )}

                {/* Mode de règlement */}
                <div className="space-y-2">
                  <Label>Mode de règlement</Label>
                  <Select
                    value={paymentData.mode_reglement}
                    onValueChange={(value) =>
                      setPaymentData({ ...paymentData, mode_reglement: value })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Comptant">Comptant</SelectItem>
                      <SelectItem value="Interac">Interac</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Personne payeur - only show for Marié(e) or Conjoint(e) de fait */}
                {(selectedClient?.situation_familiale === "Marié(e)" || selectedClient?.situation_familiale === "Conjoint(e) de fait") && (
                  <div className="space-y-2">
                    <Label>Personne payeur</Label>
                    <Select
                      value={paymentData.personne_payeur}
                      onValueChange={(value) =>
                        setPaymentData({ ...paymentData, personne_payeur: value })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Sélectionner" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Requérent principal">Requérent principal</SelectItem>
                        <SelectItem value="Conjoint/Conjointe">Conjoint/Conjointe</SelectItem>
                        <SelectItem value="Autre">Autre</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {paymentData.personne_payeur === "Autre" && (selectedClient?.situation_familiale === "Marié(e)" || selectedClient?.situation_familiale === "Conjoint(e) de fait") && (
                  <div className="space-y-2">
                    <Label>Préciser le payeur</Label>
                    <Input
                      value={paymentData.autre_payeur}
                      onChange={(e) =>
                        setPaymentData({ ...paymentData, autre_payeur: e.target.value })
                      }
                    />
                  </div>
                )}

                <Button
                  onClick={savePayment}
                  disabled={saving || !paymentData.honoraires || !paymentData.date_paiement || !paymentData.mode_reglement || ((selectedClient?.situation_familiale === "Marié(e)" || selectedClient?.situation_familiale === "Conjoint(e) de fait") && !paymentData.personne_payeur)}
                  className="w-full"
                >
                  {saving ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Enregistrement...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      {existingPayment ? "Mettre à jour le paiement" : "Enregistrer le paiement"}
                    </>
                  )}
                </Button>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Forms Section */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5 text-primary" />
                  Suivi des formulaires
                </CardTitle>
                <CardDescription>
                  Gérer les signatures des formulaires
                </CardDescription>
              </CardHeader>
              <CardContent>
                {hasSpouse ? (
                  <Tabs defaultValue="principal">
                    <TabsList className="grid w-full grid-cols-2">
                      <TabsTrigger value="principal" className="flex items-center gap-1">
                        <User className="h-4 w-4" />
                        Principal
                      </TabsTrigger>
                      <TabsTrigger 
                        value="conjoint" 
                        className="flex items-center gap-1"
                        disabled={isSpouseNonResident}
                      >
                        <Users className="h-4 w-4" />
                        Conjoint(e)
                        {isSpouseNonResident && (
                          <span className="ml-1 text-xs">(N/A)</span>
                        )}
                      </TabsTrigger>
                    </TabsList>
                    <TabsContent value="principal" className="mt-4">
                      {renderFormSection(true, principalFormData, setPrincipalFormData)}
                    </TabsContent>
                    <TabsContent value="conjoint" className="mt-4">
                      {isSpouseNonResident ? (
                        <div className="p-6 text-center rounded-lg bg-orange-50 border border-orange-200">
                          <AlertCircle className="h-8 w-8 text-orange-500 mx-auto mb-2" />
                          <p className="font-medium text-orange-700">Signature non applicable</p>
                          <p className="text-sm text-orange-600 mt-1">
                            Le/La conjoint(e) est non résident(e), la signature n'est pas requise.
                          </p>
                        </div>
                      ) : (
                        renderFormSection(false, spouseFormData, setSpouseFormData)
                      )}
                    </TabsContent>
                  </Tabs>
                ) : (
                  <div>
                    <div className="flex items-center gap-2 mb-4 text-muted-foreground">
                      <User className="h-4 w-4" />
                      <span className="text-sm font-medium">Requérant principal</span>
                    </div>
                    {renderFormSection(true, principalFormData, setPrincipalFormData)}
                  </div>
                )}
              </CardContent>
            </Card>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default ClientFilePage;
